import React, { useCallback, useEffect, useState } from "react";
import { Button, Form, Modal, Nav, ProgressBar, Spinner } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { AlertTriangle, HardDrive, RotateCw } from "lucide-react";
import {
  dockerDiskUsage,
  dockerReclaim,
  serverAutoremove,
  serverDiskUsage,
  serverReclaim,
} from "../../redux/actionCreator";

/**
 * Disk reclaim for one server, in two tabs.
 *
 *   Docker  — `docker system df` and targeted prunes (images, cache, volumes).
 *   System  — everything else that quietly fills a long-lived box: package
 *             caches, journald, rotated logs, /tmp, dev caches. When the
 *             server has Docker, its two self-regenerating pools appear here
 *             too, so "clear unwanted files" really does clear all of them.
 *
 * The design point, on both tabs: targets are NOT equally safe, so they are
 * not presented as one list with a "select all". Anything that destroys
 * something no rebuild can recreate — Docker volumes, `apt autoremove` — is
 * separated out, unticked, styled as a hazard and gated behind its own
 * confirmation.
 */

// ── Docker tab ──────────────────────────────────────────────────────────
// Safe targets: everything here regenerates on the next build or deploy.
const SAFE_TARGETS = [
  {
    id: "dangling-images",
    label: "Dangling images",
    hint: "Untagged layers left behind by rebuilds. Rebuilt on the next deploy.",
    pool: "Images",
  },
  {
    id: "build-cache",
    label: "Build cache",
    hint: "Grows on every compose deploy now that --build is on. Rebuilt on demand.",
    pool: "Build Cache",
  },
  {
    id: "stopped-containers",
    label: "Stopped containers",
    hint: "Removes every exited container — including retained releases you could revert to.",
    pool: "Containers",
    caution: true,
  },
  {
    id: "unused-networks",
    label: "Unused networks",
    hint: "Networks with nothing attached.",
    pool: null,
  },
];

// ── System tab ──────────────────────────────────────────────────────────
// Keyed by the backend's fixed category table. A key missing from here still
// renders — it just shows no hint — so the server stays the source of truth.
const CATEGORY_HINTS = {
  "package-cache": "Downloaded .deb/.rpm files kept after install. Re-fetched on demand.",
  journald: "Vacuums the system journal, keeping the most recent days.",
  "rotated-logs": "Only .gz/.xz/.old/.N archives. The live log files are never matched.",
  "temp-files": "Filtered on last-access time, so anything touched recently is left alone.",
  "dev-caches": "npm / pip / yarn download caches. Rebuilt on the next install.",
  "docker-dangling-images": "Untagged Docker layers left by rebuilds. Same as the Docker tab.",
  "docker-build-cache": "Docker's build cache. Rebuilt on demand.",
};

function humanBytes(n) {
  if (!Number.isFinite(n) || n <= 0) return "0 B";
  const u = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${u[i]}`;
}

/** The category's size, whichever form the probe returned it in. */
function sizeLabel(c) {
  if (!c.available) return "unavailable";
  if (Number.isFinite(c.bytes)) return humanBytes(c.bytes);
  return c.raw || "—";
}

function parseUsePct(s) {
  const n = parseInt(String(s || "").replace("%", ""), 10);
  return Number.isFinite(n) ? n : 0;
}

export default function DiskReclaimModal({ projectId, serverId, serverLabel, onClose }) {
  const dispatch = useDispatch();
  const [tab, setTab] = useState("docker");

  // Docker tab state
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [picked, setPicked] = useState({});
  const [wantVolumes, setWantVolumes] = useState(false);
  const [volumeConfirm, setVolumeConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  // System tab state
  const [sys, setSys] = useState(null);
  const [sysLoading, setSysLoading] = useState(false);
  const [sysErr, setSysErr] = useState(null);
  const [sysPicked, setSysPicked] = useState({});
  const [sysBusy, setSysBusy] = useState(false);
  const [sysResult, setSysResult] = useState(null);
  const [wantAutoremove, setWantAutoremove] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    const res = await dispatch(dockerDiskUsage(projectId, serverId));
    setLoading(false);
    if (res?.status) setData(res.data);
    else setErr(res?.message || "Could not read disk usage");
  }, [dispatch, projectId, serverId]);

  const loadSys = useCallback(async () => {
    setSysLoading(true);
    setSysErr(null);
    const res = await dispatch(serverDiskUsage(projectId, serverId));
    setSysLoading(false);
    if (res?.status) setSys(res.data);
    else setSysErr(res?.message || "Could not read filesystem usage");
  }, [dispatch, projectId, serverId]);

  useEffect(() => { load(); }, [load]);
  // Deferred until the tab is opened: the probe runs `du` over the whole root
  // filesystem, which is far too slow to pay for on every modal open.
  useEffect(() => { if (tab === "system" && !sys && !sysLoading) loadSys(); }, [tab, sys, sysLoading, loadSys]);

  const poolFor = (name) =>
    (data?.pools || []).find((p) => p.type?.toLowerCase() === String(name || "").toLowerCase());

  const chosen = [
    ...SAFE_TARGETS.filter((t) => picked[t.id]).map((t) => t.id),
    ...(wantVolumes && volumeConfirm.trim().toUpperCase() === "DELETE" ? ["unused-volumes"] : []),
  ];

  const sysChosen = (sys?.categories || [])
    .filter((c) => c.available && sysPicked[c.key])
    .map((c) => c.key);

  const run = async () => {
    if (!chosen.length) return;
    const volumes = chosen.includes("unused-volumes");
    const msg = volumes
      ? `Reclaim ${chosen.length} item type(s) on "${serverLabel}" — INCLUDING UNUSED VOLUMES?\n\n` +
        `Volume data cannot be recovered. A stopped database's volume counts as unused.`
      : `Reclaim ${chosen.length} item type(s) on "${serverLabel}"?`;
    if (!window.confirm(msg)) return;
    setBusy(true);
    setResult(null);
    const res = await dispatch(dockerReclaim(projectId, serverId, chosen));
    setBusy(false);
    setResult(res);
    setVolumeConfirm("");
    setWantVolumes(false);
    load();
  };

  const runSys = async () => {
    if (!sysChosen.length) return;
    const labels = (sys?.categories || [])
      .filter((c) => sysChosen.includes(c.key))
      .map((c) => `• ${c.label} (${sizeLabel(c)})`)
      .join("\n");
    if (!window.confirm(`Clear the following on "${serverLabel}"?\n\n${labels}`)) return;
    setSysBusy(true);
    setSysResult(null);
    const res = await dispatch(serverReclaim(projectId, serverId, sysChosen));
    setSysBusy(false);
    setSysResult(res);
    setSysPicked({});
    loadSys();
  };

  const runAutoremove = async () => {
    if (!window.confirm(
      `Run "apt-get autoremove --purge" on "${serverLabel}"?\n\n` +
      `This removes installed packages, not cache. Read the package list first.`,
    )) return;
    setSysBusy(true);
    setSysResult(null);
    const res = await dispatch(serverAutoremove(projectId, serverId));
    setSysBusy(false);
    setSysResult(res);
    setWantAutoremove(false);
    loadSys();
  };

  const anyBusy = busy || sysBusy;
  const onSystem = tab === "system";

  return (
    <Modal show onHide={anyBusy ? undefined : onClose} size="lg" centered>
      <Modal.Header closeButton={!anyBusy}>
        <h5 className="m-0 d-inline-flex align-items-center gap-2">
          <HardDrive size={18} /> Disk — {serverLabel}
        </h5>
      </Modal.Header>
      <Modal.Body>
        <Nav variant="tabs" activeKey={tab} className="mb-3" onSelect={(k) => !anyBusy && setTab(k)}>
          <Nav.Item><Nav.Link eventKey="docker" disabled={anyBusy}>Docker</Nav.Link></Nav.Item>
          <Nav.Item><Nav.Link eventKey="system" disabled={anyBusy}>System files</Nav.Link></Nav.Item>
        </Nav>

        {!onSystem && (
          <>
            {err && <div className="alert alert-danger">{err}</div>}

            {loading ? (
              <div className="text-center py-3"><Spinner size="sm" animation="border" /> Reading disk usage…</div>
            ) : (
              <>
                {data?.raw ? (
                  <pre className="small mb-3" style={{ whiteSpace: "pre-wrap" }}>{data.raw}</pre>
                ) : (
                  <table className="emp-table mb-3">
                    <thead>
                      <tr><th>Pool</th><th>Items</th><th>Active</th><th>Size</th><th>Reclaimable</th></tr>
                    </thead>
                    <tbody>
                      {(data?.pools || []).map((p) => (
                        <tr key={p.type}>
                          <td>{p.type}</td>
                          <td>{p.total}</td>
                          <td>{p.active}</td>
                          <td><code className="small">{p.size}</code></td>
                          <td><code className="small">{p.reclaimable}</code></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                <div className="mb-2 fw-semibold small">Safe to reclaim</div>
                {SAFE_TARGETS.map((t) => {
                  const pool = poolFor(t.pool);
                  return (
                    <Form.Check
                      key={t.id}
                      type="checkbox"
                      id={`reclaim-${t.id}`}
                      className="mb-2"
                      disabled={busy}
                      checked={!!picked[t.id]}
                      onChange={(e) => setPicked((s) => ({ ...s, [t.id]: e.target.checked }))}
                      label={
                        <>
                          {t.label}
                          {pool && <span className="text-muted small"> — {pool.reclaimable} reclaimable</span>}
                          <div className={`small ${t.caution ? "text-warning" : "text-muted"}`}>{t.hint}</div>
                        </>
                      }
                    />
                  );
                })}

                {/* Volumes are kept apart on purpose: this is the one option here
                    that destroys something no rebuild can recreate. */}
                <div className="alert alert-danger mt-3 mb-0 py-2">
                  <div className="d-inline-flex align-items-center gap-2 fw-semibold">
                    <AlertTriangle size={15} /> Unused volumes — deletes data
                  </div>
                  <div className="small mt-1">
                    Docker calls a volume "unused" when no <em>running</em> container
                    references it, so a stopped database's data volume qualifies.
                    This is never part of the nightly sweep and cannot be undone.
                    {poolFor("Local Volumes") && (
                      <> Currently <code>{poolFor("Local Volumes").reclaimable}</code> reclaimable.</>
                    )}
                  </div>
                  <Form.Check
                    type="checkbox"
                    id="reclaim-volumes"
                    className="mt-2"
                    disabled={busy}
                    checked={wantVolumes}
                    onChange={(e) => { setWantVolumes(e.target.checked); setVolumeConfirm(""); }}
                    label="I want to delete unused volumes too"
                  />
                  {wantVolumes && (
                    <Form.Control
                      className="mt-2"
                      size="sm"
                      value={volumeConfirm}
                      disabled={busy}
                      onChange={(e) => setVolumeConfirm(e.target.value)}
                      placeholder="Type DELETE to confirm"
                      style={{ maxWidth: 240, fontFamily: "ui-monospace, monospace" }}
                    />
                  )}
                </div>

                {result && (
                  <div className={`alert ${result.status ? "alert-success" : "alert-warning"} mt-3 mb-0`}>
                    <div className="fw-semibold">{result.message}</div>
                    {(result.data?.results || []).map((r) => (
                      <div key={r.target} className="small">
                        {r.ok ? "✓" : "✗"} {r.target}
                        {r.reclaimed ? ` — reclaimed ${r.reclaimed}` : ""}
                        {r.message ? ` — ${r.message}` : ""}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </>
        )}

        {onSystem && (
          <>
            {sysErr && <div className="alert alert-danger">{sysErr}</div>}

            {sysLoading ? (
              <div className="text-center py-3">
                <Spinner size="sm" animation="border" /> Measuring the filesystem…
                <div className="small text-muted mt-1">
                  This walks the whole root filesystem and can take a minute.
                </div>
              </div>
            ) : sys && (
              <>
                <div className="mb-2 fw-semibold small">Mounted filesystems</div>
                {(sys.mounts || []).map((m) => {
                  const pct = parseUsePct(m.use_pct);
                  return (
                    <div key={`${m.filesystem}-${m.mount}`} className="mb-2">
                      <div className="d-flex justify-content-between small">
                        <code>{m.mount}</code>
                        <span className="text-muted">{m.used} of {m.size} — {m.avail} free</span>
                      </div>
                      <ProgressBar
                        now={pct}
                        // Colour is the operator signal: a 92%-full root is the
                        // thing to notice first on this panel.
                        variant={pct >= 90 ? "danger" : pct >= 75 ? "warning" : "success"}
                        style={{ height: 6 }}
                      />
                    </div>
                  );
                })}

                {!!(sys.biggest || []).length && (
                  <details className="mb-3 mt-3">
                    <summary className="small fw-semibold" style={{ cursor: "pointer" }}>
                      Biggest directories
                    </summary>
                    <table className="emp-table mt-2">
                      <thead><tr><th>Size</th><th>Path</th></tr></thead>
                      <tbody>
                        {sys.biggest.map((b) => (
                          <tr key={b.path}>
                            <td><code className="small">{b.size}</code></td>
                            <td><code className="small">{b.path}</code></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </details>
                )}

                <div className="mb-2 fw-semibold small">Safe to clear</div>
                <div className="small text-muted mb-2">
                  Each row is one fixed command. There is no free-form path here,
                  so nothing outside this list can be deleted.
                </div>
                {(sys.categories || []).map((c) => (
                  <Form.Check
                    key={c.key}
                    type="checkbox"
                    id={`sys-${c.key}`}
                    className="mb-2"
                    disabled={sysBusy || !c.available}
                    checked={!!sysPicked[c.key]}
                    onChange={(e) => setSysPicked((s) => ({ ...s, [c.key]: e.target.checked }))}
                    label={
                      <>
                        {c.label}
                        <span className="text-muted small"> — {sizeLabel(c)}</span>
                        <div className="small text-muted">{CATEGORY_HINTS[c.key] || ""}</div>
                      </>
                    }
                  />
                ))}
                <div className="small text-muted mt-2">
                  Stopped containers are not listed here: this portal keeps the
                  last 5 release containers so a deploy can be reverted. Clear
                  them from the Docker tab if you mean to give that up.
                </div>

                {/* The only action on this panel that removes software rather
                    than disposable cache, so it is separated and unticked. */}
                <div className="alert alert-warning mt-3 mb-0 py-2">
                  <div className="d-inline-flex align-items-center gap-2 fw-semibold">
                    <AlertTriangle size={15} /> Remove unused packages — not cache
                  </div>
                  <div className="small mt-1">
                    <code>apt-get autoremove --purge</code> uninstalls packages nothing
                    depends on any more. That judgement is the package manager's, so
                    read the list before running it.
                  </div>
                  <Form.Check
                    type="checkbox"
                    id="sys-autoremove"
                    className="mt-2"
                    disabled={sysBusy || !sys.autoremove_preview}
                    checked={wantAutoremove}
                    onChange={(e) => setWantAutoremove(e.target.checked)}
                    label={
                      sys.autoremove_preview
                        ? "Show me what autoremove would do"
                        : "Nothing to autoremove on this server"
                    }
                  />
                  {wantAutoremove && sys.autoremove_preview && (
                    <>
                      <pre className="small mt-2 mb-2" style={{ whiteSpace: "pre-wrap", maxHeight: 160, overflow: "auto" }}>
                        {sys.autoremove_preview}
                      </pre>
                      <Button size="sm" variant="warning" disabled={sysBusy} onClick={runAutoremove}>
                        Run autoremove
                      </Button>
                    </>
                  )}
                </div>

                {sysResult && (
                  <div className={`alert ${sysResult.status ? "alert-success" : "alert-warning"} mt-3 mb-0`}>
                    <div className="fw-semibold">{sysResult.message}</div>
                    {(sysResult.data?.results || []).map((r) => (
                      <div key={r.target} className="small">
                        {r.ok ? "✓" : "✗"} {r.label || r.target}
                        {r.output ? ` — ${r.output}` : ""}
                      </div>
                    ))}
                    {sysResult.data?.output && (
                      <pre className="small mt-2 mb-0" style={{ whiteSpace: "pre-wrap" }}>
                        {sysResult.data.output}
                      </pre>
                    )}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="light"
          onClick={onSystem ? loadSys : load}
          disabled={anyBusy || loading || sysLoading}
        >
          <RotateCw size={14} className="me-1" /> Refresh
        </Button>
        <Button variant="light" onClick={onClose} disabled={anyBusy}>Close</Button>
        {onSystem ? (
          <Button variant="primary" onClick={runSys} disabled={sysBusy || !sysChosen.length}>
            {sysBusy ? <Spinner size="sm" animation="border" /> : `Clear ${sysChosen.length || ""}`.trim()}
          </Button>
        ) : (
          <Button
            variant={chosen.includes("unused-volumes") ? "danger" : "primary"}
            onClick={run}
            disabled={busy || !chosen.length}
          >
            {busy ? <Spinner size="sm" animation="border" /> : `Reclaim ${chosen.length || ""}`.trim()}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}
